import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import reviewService from '../../api/reviewService';
import { useNotifications } from '../../context/NotificationContext';
import StatusBadge from '../common/StatusBadge';
import Pagination from '../common/Pagination';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';
import { currency, dateTime } from '../../utils/formatters';

const TABS = ['AWAITING_APPROVAL', 'PENDING', 'REJECTED', ''];

export default function BookingApproval() {
  const { t } = useTranslation();
  const notify = useNotifications();
  const [tab, setTab] = useState('AWAITING_APPROVAL');
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ content: [], totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    reviewService.bookingQueue({ page, size: 10, ...(tab ? { status: tab } : {}) })
      .then(setData)
      .catch((e) => notify.apiError(e))
      .finally(() => setLoading(false));
  }, [page, tab, notify]);

  useEffect(() => { load(); }, [load]);

  const approve = async (b) => {
    setBusy(b.id);
    try {
      await reviewService.approveBooking(b.id);
      notify.success(t('booking.status.PENDING'));
      load();
    } catch (e) { notify.apiError(e); } finally { setBusy(null); }
  };

  const reject = async (b) => {
    const reason = window.prompt(`${t('booking.actions.REJECTED')} ? (${t('booking.reason')}, ${t('common.optional')})`, '');
    if (reason === null) return;
    setBusy(b.id);
    try {
      await reviewService.rejectBooking(b.id, reason);
      notify.success(t('booking.status.REJECTED'));
      load();
    } catch (e) { notify.apiError(e); } finally { setBusy(null); }
  };

  return (
    <div className="page">
      <div className="container stack" style={{ gap: '1.25rem' }}>
        <div className="page-head">
          <div>
            <h1>{t('admin.reviewBookingTitle')}</h1>
            <p>{t('admin.reviewBookingSub')}</p>
          </div>
        </div>

        <div className="toolbar">
          {TABS.map((s) => (
            <button key={s || 'all'} className={`chip ${tab === s ? 'active' : ''}`}
              onClick={() => { setTab(s); setPage(0); }}>
              {s ? t(`booking.status.${s}`) : t('common.all')}
            </button>
          ))}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : data.content.length === 0 ? (
          <EmptyState icon="clipboard-check" title={t('admin.queueClear')} />
        ) : (
          <div className="stack">
            {data.content.map((b) => (
              <div key={b.id} className="card pad stack" style={{ gap: '.5rem' }}>
                <div className="spread">
                  <div>
                    <strong>{b.clientName}</strong>
                    <span className="muted" style={{ fontSize: '.88rem' }}> → {b.technicianName} ({b.technicianTypeName})</span>
                  </div>
                  <StatusBadge status={b.status} />
                </div>
                <p>{b.description}</p>
                <div className="row" style={{ gap: '1.5rem', fontSize: '.88rem' }}>
                  <span className="muted"><i className="bi bi-calendar-event" /> {dateTime(b.scheduledDate)}</span>
                  <span className="muted"><i className="bi bi-geo-alt" /> {b.clientAddress}</span>
                  {b.estimatedCost != null && <span className="muted">{t('client.budget')} {currency(b.estimatedCost)}</span>}
                  {b.clientPhone && <span className="muted"><i className="bi bi-telephone" /> {b.clientPhone}</span>}
                </div>
                {b.notes && <p className="muted" style={{ fontSize: '.9rem' }}>{b.notes}</p>}
                {b.rejectionReason && <p className="muted" style={{ fontSize: '.9rem' }}>{t('booking.reason')}: {b.rejectionReason}</p>}
                {b.status === 'AWAITING_APPROVAL' && (
                  <div className="row" style={{ gap: '.5rem' }}>
                    <button className="btn sm accent" disabled={busy === b.id} onClick={() => approve(b)}>{t('common.approve')}</button>
                    <button className="btn sm danger" disabled={busy === b.id} onClick={() => reject(b)}>{t('common.reject')}</button>
                  </div>
                )}
                {b.reviewedBy && (
                  <span className="muted" style={{ fontSize: '.8rem' }}>{t('admin.reviewedBy', { who: b.reviewedBy })}</span>
                )}
              </div>
            ))}
          </div>
        )}
        <Pagination page={page} totalPages={data.totalPages} onChange={setPage} />
      </div>
    </div>
  );
}
